// Input abstraction. Exposes the virtual key flags identical to the C
// `key_held[]` array, fed by both keyboard and (later) touch controls. Discrete
// key presses are queued and drained by HandleEvents(), mirroring SDL's event
// poll, so toggles (map, pause, quit, help) behave exactly like the original.

import { K_UP, K_DN, K_LT, K_RT, K_SP } from './constants';

export type Sym =
  | 'UP'
  | 'DN'
  | 'LT'
  | 'RT'
  | 'SP'
  | 'RETURN'
  | 'ESCAPE'
  | 'TAB'
  | 'H'
  | 'I'
  | 'P';

export interface KeyEvent {
  down: boolean;
  sym: Sym;
}

export interface InputFrame {
  held: ReadonlyArray<0 | 1>;
  pressed: ReadonlySet<Sym>;
  released: ReadonlySet<Sym>;
}

// key_held[10]
export const keyHeld: number[] = new Array(10).fill(0);

let queue: KeyEvent[] = [];

const HELD_SYMS: Array<{ sym: Sym; key: number }> = [
  { sym: 'UP', key: K_UP },
  { sym: 'DN', key: K_DN },
  { sym: 'LT', key: K_LT },
  { sym: 'RT', key: K_RT },
  { sym: 'SP', key: K_SP },
];

function symToHeld(sym: Sym): number | null {
  const row = HELD_SYMS.find((h) => h.sym === sym);
  return row ? row.key : null;
}

/** Snapshot keyboard/touch state for one simulation tick. */
export function pollInput(): InputFrame {
  const pressed = new Set<Sym>();
  const released = new Set<Sym>();
  for (const ev of takeEvents()) {
    if (ev.down) pressed.add(ev.sym);
    else released.add(ev.sym);
    const k = symToHeld(ev.sym);
    if (k !== null) keyHeld[k] = ev.down ? 1 : 0;
  }
  const held = keyHeld.map((v) => (v ? 1 : 0)) as Array<0 | 1>;
  return { held, pressed, released };
}

export function applyInputFrameToKeyHeld(frame: InputFrame): void {
  for (const { sym, key } of HELD_SYMS) {
    keyHeld[key] = frame.held[key] ? 1 : 0;
  }
  for (const sym of frame.pressed) {
    const k = symToHeld(sym);
    if (k !== null) keyHeld[k] = 1;
  }
  for (const sym of frame.released) {
    const symHeld = symToHeld(sym);
    if (symHeld !== null) keyHeld[symHeld] = 0;
  }
}

export function pushEvent(ev: KeyEvent): void {
  queue.push(ev);
}

export function takeEvents(): KeyEvent[] {
  const q = queue;
  queue = [];
  return q;
}

export function clearInput(): void {
  keyHeld[K_SP] = 0;
  keyHeld[K_UP] = 0;
  keyHeld[K_DN] = 0;
  keyHeld[K_LT] = 0;
  keyHeld[K_RT] = 0;
}

function shouldCaptureGameKey(e: KeyboardEvent): boolean {
  const t = e.target;
  if (t instanceof HTMLElement) {
    if (t.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) {
      return false;
    }
    if (document.getElementById('meritous-auth')) {
      return false;
    }
  }
  return true;
}

const KEYMAP: Record<string, Sym> = {
  ArrowUp: 'UP',
  KeyW: 'UP',
  ArrowDown: 'DN',
  KeyS: 'DN',
  ArrowLeft: 'LT',
  KeyA: 'LT',
  ArrowRight: 'RT',
  KeyD: 'RT',
  Space: 'SP',
  Enter: 'RETURN',
  NumpadEnter: 'RETURN',
  Escape: 'ESCAPE',
  Tab: 'TAB',
  KeyH: 'H',
  KeyI: 'I',
  KeyP: 'P',
};

export function initInput(): void {
  window.addEventListener(
    'keydown',
    (e) => {
      const sym = KEYMAP[e.code];
      if (!sym || !shouldCaptureGameKey(e)) return;
      e.preventDefault();
      if (e.repeat) return; // SDL delivers one KEYDOWN per physical press
      pushEvent({ down: true, sym });
    },
    { passive: false }
  );
  window.addEventListener(
    'keyup',
    (e) => {
      const sym = KEYMAP[e.code];
      if (!sym || !shouldCaptureGameKey(e)) return;
      e.preventDefault();
      pushEvent({ down: false, sym });
    },
    { passive: false }
  );
}
